import { api as mondayApi } from './lib/mondayApi';

/**
 * Where the backend is: REACT_APP_API_BASE_URL when it is set (a local tunnel
 * such as "https://….trycloudflare.com", set in .env.development.local so a
 * production build never picks it up), else "api", the path the app's own host
 * proxies to the backend. Read at build time; a trailing slash is ignored.
 */
export const API_BASE = String(process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '') || 'api';

/** A backend path ('templates/…') as a URL under API_BASE. */
const apiUrl = (path) => `${API_BASE}/${path}`;

/**
 * Single upsert used by the template editors (POST /templates/save).
 * payload: { id?, name, access_level, type?, columns, subitems, content? }
 *   type     'subitem' (default) or 'group'
 *   content  the group template body; ignored for subitem templates
 * The backend validates the payload against lib/templateLimits and answers
 * 400 { error } with the reason when it is over a limit.
 */
const saveTemplate = async (sessionToken, payload) => {
	let res = await fetch(apiUrl('templates/save/') + sessionToken, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(payload),
	});

	return await res.json();
};

const getTemplates = async (sessionToken) => {
	let res = await fetch(apiUrl('templates/') + sessionToken);
	return await res.json();
};

const deleteTemplate = async (sessionToken, id) => {
	const data = {
		id: id,
	};

	let res = await fetch(apiUrl('templates/delete/') + sessionToken, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json', // Chỉ định là gửi JSON
		},
		body: JSON.stringify(data), // Chuyển đối tượng thành chuỗi JSON
	});

	return await res.json(); // Đảm bảo là nhận dữ liệu JSON
};

const templateOperation = async (sessionToken, operation, template_id) => {
	const data = {
		operation: operation,
		template_id: template_id,
	};

	let res = await fetch(apiUrl('templates/operation/') + sessionToken, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json', // Chỉ định là gửi JSON
		},
		body: JSON.stringify(data), // Chuyển đối tượng thành chuỗi JSON
	});

	return await res.json(); // Đảm bảo là nhận dữ liệu JSON
};

/**
 * For when the check cannot be MADE at all: our own endpoint 500s, a proxy
 * answers with an HTML error page, the body will not parse, or the network
 * drops. No `code`, so the alert says the subscription could not be checked
 * (lib/subscriptionVerdict) rather than that it lapsed.
 *
 * Refuses on purpose. The backend decides the policy (spt/src/helpers/trial.ts,
 * NO_VERDICT): it asks the BeelabX hub, lets the user through when the HUB is
 * down (fail-open), and refuses only when it could not ask at all. Our backend
 * not answering is that last case seen from here, so this agrees with it.
 */
const NO_VERDICT = {
	status: false,
	message: 'Could not verify this account\u2019s subscription. Please try again shortly.',
};

/**
 * Subscription verdict for this account: { status, message?, code? }, the
 * backend's TrialResult from the BeelabX hub's apps API — see
 * lib/subscriptionVerdict for the fields and what `code` means.
 *
 * Always resolves to that shape — never null, never rejects — so callers have one
 * thing to handle. An unverifiable subscription comes back as NO_VERDICT above.
 */
const trialStatus = async (sessionToken, itemId) => {
	try {
		const res = await fetch(apiUrl('check_purchase/') + itemId + '/' + sessionToken);

		if (!res.ok) {
			console.error('check_purchase failed with status', res.status);
			return NO_VERDICT;
		}

		// A proxy/error page can return HTML with a 200, so parse defensively.
		const text = await res.text();
		if (text.includes('<html>')) {
			console.error('check_purchase returned HTML instead of JSON');
			return NO_VERDICT;
		}

		const verdict = JSON.parse(text);
		// Parsed, but a body with no boolean status is not a verdict either.
		if (typeof verdict?.status !== 'boolean') {
			console.error('check_purchase returned no status field:', verdict);
			return NO_VERDICT;
		}
		return verdict;
	} catch (error) {
		console.error('check_purchase failed:', error);
		return NO_VERDICT;
	}
};

/**
 * In-platform notification (bell icon).
 *
 * Exactly the four arguments create_notification accepts — no `internal`, which
 * is not in the schema and fails the whole mutation.
 * https://developer.monday.com/api-reference/reference/notifications
 *
 * monday reports GraphQL failures as HTTP 200 + errors[] (lib/mondayApi keeps
 * that envelope), so the response has to be inspected — awaiting the call alone
 * reports success for a mutation that never ran. Resolves true only when monday
 * confirms the notification, and never rejects: a notification nobody received
 * must not take down its caller.
 */
const createNotification = async (userId, itemId, text) => {
	if (typeof text !== 'string' || !text.trim()) {
		console.error('create_notification skipped: no text');
		return false;
	}

	try {
		// JSON.stringify escapes quotes, backslashes and newlines in the message.
		const res = await mondayApi(
			`mutation {
				create_notification(user_id: ${userId}, target_id: ${itemId}, text: ${JSON.stringify(text)}, target_type: Project) {
					text
				}
			}`
		);

		if (res?.errors) {
			console.error('create_notification failed', res.errors);
			return false;
		}
		return Boolean(res?.data?.create_notification);
	} catch (e) {
		console.error('create_notification error', e);
		return false;
	}
};

/**
 * Files uploaded into templates (file-column feature; see lib/files for the
 * whole upload flow). Each answers the server's JSON, which is { error } on a
 * refusal — callers read it, these never throw on a 4xx.
 */
const postJson = async (path, body) => {
	const res = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
	return await res.json();
};

/** → { fileId, url, headers, expiresIn } or { error } */
const signFile = (sessionToken, { name, size }) => postJson(apiUrl('files/sign/') + sessionToken, { name, size });

/** → { fileId, name, size, mime } or { error } */
const confirmFile = (sessionToken, fileId) => postJson(apiUrl('files/confirm/') + sessionToken, { fileId });

/** → { ok } or { error } */
const deleteFile = (sessionToken, fileId) => postJson(apiUrl('files/delete/') + sessionToken, { fileId });

/** → { files, bytes, limits, plan: { tier, name, supportsFiles } | null } */
const filesUsage = async (sessionToken) => {
	const res = await fetch(apiUrl('files/usage/') + sessionToken);
	return await res.json();
};

/** → { url } (valid for a minute, downloads as an attachment) or { error } */
const fileDownloadUrl = async (sessionToken, fileId) => {
	const res = await fetch(apiUrl('files/') + encodeURIComponent(fileId) + '/download/' + sessionToken);
	return await res.json();
};

/**
 * The files stage of a browser apply (lib/applyFiles): the server streams the
 * template's files into the created rows. → { planned, copied, failures } or
 * { error: 'not_connected' } (HTTP 409) when the user has not connected.
 */
const applyFiles = (sessionToken, { templateId, targets }) => postJson(apiUrl('template/apply-files/') + sessionToken, { templateId, targets });

/** → { connected } — whether this user has connected the app to monday (OAuth). */
const oauthStatus = async (sessionToken) => {
	const res = await fetch(apiUrl('oauth/status/') + sessionToken);
	return await res.json();
};

/**
 * The page that starts the OAuth connection, as an absolute URL.
 * returnToApp: the iframe navigates there itself and the callback brings it
 * back into the app (lib/oauthArrival); otherwise it is opened in a new tab.
 */
const oauthStartUrl = (sessionToken, { returnToApp = false } = {}) =>
	new URL(apiUrl('oauth/start/') + sessionToken + (returnToApp ? '?return=app' : ''), document.baseURI).href;

export {
	getTemplates,
	saveTemplate,
	deleteTemplate,
	templateOperation,
	trialStatus,
	createNotification,
	signFile,
	confirmFile,
	deleteFile,
	filesUsage,
	fileDownloadUrl,
	applyFiles,
	oauthStatus,
	oauthStartUrl,
};
